import { useMemo, useState } from "react";
import { TYPEN } from "../kern/konstanten.js";
import { alsWahl, passtEinerWahl, passtWahl } from "../kern/filterwahl.js";
import { jahrLabel } from "../kern/format.js";
import { MehrfachAuswahl } from "./mehrfachauswahl.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";

/* ================================================================
   Filter und Sortierung für die geteilten Ansichten.

   Bewusst nur auf Angaben, die auch wirklich geteilt werden: Lagerort,
   Einkaufspreis, Favoriten und Bewertungen verlassen den eigenen
   Keller nie und tauchen hier deshalb nicht auf. Was im Datensatz
   fehlt — etwa Rebsorten auf einer Weinkarte oder das Trinkfenster —,
   wird gar nicht erst als Filter angeboten.

   Erwartet Zeilen dieser Form:
     { name, typ, winzerName, regionName, land, rebsorten[],
       jahrgaenge: [{ jahr, bestand, trinkVon, trinkBis }],
       bestand, preis }
   ================================================================ */

const heute = new Date().getFullYear();
const REIFEN = [["jung", "Noch nicht trinkreif"], ["reif", "Trinkreif"], ["ueber", "Überfällig"]];

function reifeVon(jg) {
  const v = jg.trinkVon, b = jg.trinkBis;
  if (v == null && b == null) return null;
  if (v != null && heute < v) return "jung";
  if (b != null && heute > b) return "ueber";
  return "reif";
}

/* Reife einer Zeile: trinkreif schlägt überfällig schlägt jung. */
function reifeZeile(z) {
  const stufen = (z.jahrgaenge || []).map(reifeVon).filter(Boolean);
  if (stufen.includes("reif")) return "reif";
  if (stufen.includes("ueber")) return "ueber";
  if (stufen.includes("jung")) return "jung";
  return null;
}

const maxJahr = (z) => (z.jahrgaenge || []).reduce((m, j) => Math.max(m, j.jahr || 0), 0);

function useOeffFilter(zeilen) {
  const [suche, setSuche] = useState("");
  /* Mehrfachwahlen: Liste, leer heißt alle (kern/filterwahl.js). */
  const [typ, setTyp] = useState([]);
  const [land, setLand] = useState([]);
  const [region, setRegion] = useState([]);
  const [winzer, setWinzer] = useState([]);
  const [rebsorte, setRebsorte] = useState([]);
  const [label, setLabel] = useState([]);
  const [reife, setReife] = useState([]);
  const [jahrgang, setJahrgang] = useState([]);
  const [sortierung, setSortierung] = useState("name-az");

  const [filterOffen, setFilterOffen] = useState(false);
  const [sortOffen, setSortOffen] = useState(false);

  /* Prüft eine Zeile gegen alle Filter außer einem. So richten sich
     die Auswahllisten nach dem, was noch übrig bleibt: Ist Frankreich
     gewählt, stehen nur noch französische Regionen zur Wahl. */
  const passtOhne = (z, aus) => {
    if (aus !== "typ" && !passtWahl(typ, z.typ)) return false;
    if (aus !== "land" && !passtWahl(land, z.land)) return false;
    if (aus !== "region" && !passtWahl(region, z.regionName)) return false;
    if (aus !== "winzer" && !passtWahl(winzer, z.winzerName)) return false;
    if (aus !== "rebsorte" && !passtEinerWahl(rebsorte, z.rebsorten)) return false;
    if (aus !== "label" && !passtWahl(label, z.label || "")) return false;
    if (aus !== "reife" && !passtWahl(reife, reifeZeile(z))) return false;
    if (aus !== "jahrgang" && !passtEinerWahl(jahrgang, (z.jahrgaenge || []).map((j) => j.jahr))) return false;
    const q = suche.trim().toLowerCase();
    if (q) {
      const heu = `${z.name} ${z.winzerName || ""} ${z.regionName || ""} ${z.land || ""} ${(z.rebsorten || []).join(" ")} ${z.label || ""}`;
      if (!heu.toLowerCase().includes(q)) return false;
    }
    return true;
  };

  /* Auswahllisten aus den Daten selbst — was nicht vorkommt, wird
     auch nicht angeboten. */
  const o = useMemo(() => {
    const laender = new Set(), regionen = new Set(), winzerNamen = new Set();
    const rebsorten = new Set(), jahre = new Set(), typen = new Set(), labels = new Set();
    let hatBestand = false, hatPreis = false, hatReife = false, ohneLabel = false;
    for (const z of zeilen) {
      if (z.land && passtOhne(z, "land")) laender.add(z.land);
      if (z.regionName && passtOhne(z, "region")) regionen.add(z.regionName);
      if (z.winzerName && passtOhne(z, "winzer")) winzerNamen.add(z.winzerName);
      if (passtOhne(z, "rebsorte")) for (const r of z.rebsorten || []) if (r) rebsorten.add(r);
      if (z.label && passtOhne(z, "label")) labels.add(z.label);
      if (!z.label) ohneLabel = true;
      if (passtOhne(z, "jahrgang")) {
        for (const j of z.jahrgaenge || []) if (j.jahr != null) jahre.add(j.jahr);
      }
      for (const j of z.jahrgaenge || []) {
        if (j.trinkVon != null || j.trinkBis != null) hatReife = true;
      }
      if (z.typ && passtOhne(z, "typ")) typen.add(z.typ);
      if (z.bestand != null) hatBestand = true;
      if (z.preis != null) hatPreis = true;
    }
    const sortiert = (s) => [...s].sort((a, b) => String(a).localeCompare(String(b), "de"));
    return {
      laender: sortiert(laender), regionen: sortiert(regionen), winzer: sortiert(winzerNamen),
      rebsorten: sortiert(rebsorten), labels: sortiert(labels), jahre: [...jahre].sort((a, b) => b - a),
      typen: [...typen], hatBestand, hatPreis, hatReife, ohneLabel,
    };
  }, [zeilen, suche, typ, land, region, winzer, rebsorte, label, reife, jahrgang]);

  /* Wird ein übergeordneter Filter geändert, dürfen untergeordnete
     nicht auf einem Wert stehen bleiben, den es nicht mehr gibt.
     Gestrichen wird nur, was zu keinem gewählten Land (keiner
     gewählten Region) mehr gehört — wer ein zweites Land dazunimmt,
     behält seine Regionen. */
  const behalte = (wahl, feld, zeilenOk) => wahl.filter((x) => zeilenOk.some((z) => z[feld] === x));
  const waehleLand = (neu) => {
    setLand(neu);
    const ok = zeilen.filter((z) => passtWahl(neu, z.land));
    setRegion((r) => behalte(r, "regionName", ok));
    setWinzer((w) => behalte(w, "winzerName", ok));
  };
  const waehleRegion = (neu) => {
    setRegion(neu);
    const ok = zeilen.filter((z) => passtWahl(land, z.land) && passtWahl(neu, z.regionName));
    setWinzer((w) => behalte(w, "winzerName", ok));
  };

  const aktiv = !!(typ.length || land.length || region.length || winzer.length || rebsorte.length
    || label.length || reife.length || jahrgang.length);
  const reset = () => {
    setTyp([]); setLand([]); setRegion([]); setWinzer([]); setRebsorte([]); setLabel([]); setReife([]); setJahrgang([]);
  };

  const gefiltert = useMemo(() => {
    /* passtOhne ohne Ausnahme = alle Filter samt Suche. */
    const liste = zeilen.filter((z) => passtOhne(z, null));
    const nachName = (a, b) => String(a.name).localeCompare(String(b.name), "de");
    const sortierer = {
      "name-az": nachName,
      "name-za": (a, b) => nachName(b, a),
      "jahr-neu": (a, b) => maxJahr(b) - maxJahr(a) || nachName(a, b),
      "jahr-alt": (a, b) => maxJahr(a) - maxJahr(b) || nachName(a, b),
      "bestand-ab": (a, b) => (b.bestand || 0) - (a.bestand || 0) || nachName(a, b),
      "preis-ab": (a, b) => (b.preis ?? -1) - (a.preis ?? -1) || nachName(a, b),
      "preis-auf": (a, b) => (a.preis ?? Infinity) - (b.preis ?? Infinity) || nachName(a, b),
    };
    return [...liste].sort(sortierer[sortierung] || nachName);
  }, [zeilen, suche, typ, land, region, winzer, rebsorte, label, reife, jahrgang, sortierung]);

  const sortAktiv = sortierung !== "name-az";
  const kompakt = { className: "ist-kompakt" };

  const auswahl = (wert, setzer, beschriftung, kinder) => (
    <select className="wl-auswahl" value={wert} onChange={(e) => setzer(e.target.value)} aria-label={beschriftung}>
      {kinder}
    </select>
  );

  const leiste = (
    <div className="wl-oefffilter">
      <div className="wl-oefffilter-kopf">
        <input className="wl-eingabe wl-oeff-suche" value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder={t("Suchen (Wein, Winzer, Region) …")} />
        <button className={"wl-filterknopf" + (filterOffen ? " ist-offen" : "") + (aktiv ? " ist-aktiv" : "")}
          aria-expanded={filterOffen} onClick={() => setFilterOffen((f) => !f)}>
          {t("Filter")}{aktiv && <span className="wl-filterpunkt" aria-hidden="true" />}
        </button>
        <button className={"wl-filterknopf" + (sortOffen ? " ist-offen" : "") + (sortAktiv ? " ist-aktiv" : "")}
          aria-expanded={sortOffen} onClick={() => setSortOffen((f) => !f)}>
          {t("Sortierung")}{sortAktiv && <span className="wl-filterpunkt" aria-hidden="true" />}
        </button>
      </div>

      {filterOffen && (
        <div className="wl-oefffilter-reihe">
          {(o.typen.length > 1 || typ.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={typ} setWahl={setTyp} alleText={t("Alle Typen")} beschriftung={t("Nach Weintyp filtern")}
              optionen={o.typen.map((k) => ({ wert: k, label: TYPEN[k] ? t(TYPEN[k].label) : k }))}
              beschrifte={(k) => (TYPEN[k] ? t(TYPEN[k].label) : k)} />
          )}
          {(o.jahre.length > 1 || jahrgang.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={jahrgang} setWahl={setJahrgang} alleText={t("Alle Jahrgänge")} beschriftung={t("Nach Jahrgang filtern")}
              optionen={o.jahre.map((j) => ({ wert: String(j), label: jahrLabel(j) }))}
              beschrifte={(j) => jahrLabel(Number(j))} />
          )}
          {(o.laender.length > 1 || land.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={land} setWahl={waehleLand} alleText={t("Alle Länder")} beschriftung={t("Nach Land filtern")}
              optionen={o.laender.map((l) => ({ wert: l, label: landName(l) }))} beschrifte={landName} />
          )}
          {(o.regionen.length > 1 || region.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={region} setWahl={waehleRegion} alleText={t("Alle Regionen")} beschriftung={t("Nach Region filtern")}
              optionen={o.regionen.map((r) => ({ wert: r, label: r }))} />
          )}
          {(o.winzer.length > 1 || winzer.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={winzer} setWahl={setWinzer} alleText={t("Alle Winzer")} beschriftung={t("Nach Winzer filtern")}
              optionen={o.winzer.map((w) => ({ wert: w, label: w }))} />
          )}
          {(o.rebsorten.length > 1 || rebsorte.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={rebsorte} setWahl={setRebsorte} alleText={t("Alle Rebsorten")} beschriftung={t("Nach Rebsorte filtern")}
              optionen={o.rebsorten.map((r) => ({ wert: r, label: r }))} />
          )}
          {(o.labels.length > 1 || (o.labels.length === 1 && o.ohneLabel) || label.length > 0) && (
            <MehrfachAuswahl {...kompakt} wahl={label} setWahl={setLabel} alleText={t("Alle Labels")} beschriftung={t("Nach Label filtern")}
              optionen={o.labels.map((l) => ({ wert: l, label: l }))} />
          )}
          {o.hatReife && (
            <MehrfachAuswahl {...kompakt} wahl={reife} setWahl={setReife} alleText={t("Jede Trinkreife")} beschriftung={t("Nach Trinkreife filtern")}
              optionen={REIFEN.map(([k, lbl]) => ({ wert: k, label: t(lbl) }))} />
          )}
          {aktiv && <button className="wl-oefffilter-reset" onClick={reset}>{t("Zurücksetzen")}</button>}
        </div>
      )}

      {sortOffen && (
        <div className="wl-oefffilter-reihe">
          {auswahl(sortierung, setSortierung, "Sortierung", (
            <>
              <option value="name-az">{t("Name A–Z")}</option>
              <option value="name-za">{t("Name Z–A")}</option>
              {o.jahre.length > 1 && <option value="jahr-neu">{t("Jahrgang, neueste zuerst")}</option>}
              {o.jahre.length > 1 && <option value="jahr-alt">{t("Jahrgang, älteste zuerst")}</option>}
              {o.hatBestand && <option value="bestand-ab">{t("Bestand, meiste zuerst")}</option>}
              {o.hatPreis && <option value="preis-auf">{t("Preis aufsteigend")}</option>}
              {o.hatPreis && <option value="preis-ab">{t("Preis absteigend")}</option>}
            </>
          ))}
        </div>
      )}
    </div>
  );

  /* Von außen (Sprung aus der Winzer- oder Regionsliste) kommt ein
     einzelner Name oder "" — als Wahl mit einem Element oder leer. */
  return {
    zeilen: gefiltert, leiste, aktiv, suche,
    setWinzerFilter: (n) => setWinzer(alsWahl(n)),
    setRegionFilter: (n) => waehleRegion(alsWahl(n)),
  };
}

export { useOeffFilter };
